import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";

import { IGiveawayAuto } from "../../../interfaces";
import { EmitError } from "../../../utils/EmitError";
import useAuth from "../../../hooks/useAuth";
import useModules from "../../../hooks/useModules";
import { useReactToPrint } from "react-to-print";
import { ITicketDistribution } from "../../../services/http/services/TicketsService";
import { styledReportStatsGiveaway } from "./styledReportStatsGiveaway";
import GiveawaysAutoService from "../../../services/http/services/GiveawaysAutoService";
import TicketsAutoService from "../../../services/http/services/TicketsAutoService";

export interface IStatsGiveawayAuto {
    total_participants: number;
    total_tickets: number;
    total_taxes: number;
}

export default function useViewGiveawayAuto() {
    const { id } = useParams();
    const componentToPrintRef = useRef<HTMLDivElement>(null);
    const [searchParams] = useSearchParams();
    const activeTab = searchParams.get("tab") ?? '1';
    const [giveawayAutoData, setGiveawayAutoData] = useState<IGiveawayAuto>();
    const [previousGiveawayAutoData, setPreviousGiveawayAutoData] = useState<IGiveawayAuto | null>();
    const [ticketDistribution, setTicketDistribution] = useState<ITicketDistribution[]>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [buttonReportDisabled, setButtonReportDisabled] = useState<boolean>(true);
    const [statsGiveawayAuto, setStatsGiveawayAuto] = useState<IStatsGiveawayAuto>({
        total_participants: 0,
        total_tickets: 0,
        total_taxes: 0
    });
    const [statsPreviousGiveawayAuto, setStatsPreviousGiveawayAuto] = useState<IStatsGiveawayAuto>({
        total_participants: 0,
        total_tickets: 0,
        total_taxes: 0
    });

    const { activeCity } = useAuth();
    const { hasRewardModule, hasTaxesModule } = useModules();
    const navigate = useNavigate();

    const handlePrint = useReactToPrint({
        content: () => componentToPrintRef.current,
        documentTitle: 'Relatório do sorteio',
        pageStyle: styledReportStatsGiveaway
    });

    const onChartAnimationEnd = useCallback(() => {
        setButtonReportDisabled(false);
    }, []);

    const getStatsGiveaway = useCallback(async (data: typeof giveawayAutoData | typeof previousGiveawayAutoData) => {
        if (!data || !activeCity) return null;

        return {
            total_tickets: data.total_tickets ?? 0,
            total_taxes: data.total_taxes ?? 0,
            total_participants: data.total_user_participating ?? 0,
        };
    }, [activeCity]);

    const canViewWinners = useMemo(() => {

        const statusThatCanAccess = ["Em verificação", "Finalizado"];

        if (!giveawayAutoData || !statusThatCanAccess.includes(giveawayAutoData.status) || !giveawayAutoData.is_valid) {
            return false;
        }

        return true;
    }, [giveawayAutoData]);




    useEffect(() => {
        const controller = new AbortController();

        async function loadData() {
            try {
                setIsLoading(true);

                if (!activeCity) {
                    return;
                }

                const signal = controller.signal;
                const giveawayAutoId = Number(id);

                const [giveawayAutoData, ticketDistribution, previousGiveawayData] = await Promise.all([
                    GiveawaysAutoService.getGiveawayById({ id: giveawayAutoId, signal }),
                    TicketsAutoService.getTicketDistributionByRaffle({ giveawayAutoId, signal }),
                    GiveawaysAutoService.getPreviousGiveawayAutoByCurrent({ currentGiveawayAutoId: giveawayAutoId, cityId: activeCity.id!, signal }),
                ]);


                if (giveawayAutoData) {
                    const stats = await getStatsGiveaway(giveawayAutoData);
                    setStatsGiveawayAuto(stats!);
                }

                if (previousGiveawayData) {
                    const statsPrev = await getStatsGiveaway(previousGiveawayData);
                    setStatsPreviousGiveawayAuto(statsPrev!);
                }



                setTicketDistribution(ticketDistribution);
                setGiveawayAutoData(giveawayAutoData);
                setPreviousGiveawayAutoData(previousGiveawayData);

                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                setIsLoading(false);

                EmitError(err, true, 'view_sorteio_auto_opasdasjpdasd');

                navigate('/sorteios/auto');
            }
        }

        loadData();

        return () => {
            controller.abort();
        }
    }, [id, navigate, getStatsGiveaway, activeCity, hasRewardModule, hasTaxesModule]);

    return {
        isLoading,
        statsGiveawayAuto,
        statsPreviousGiveawayAuto,
        giveawayAutoData,
        previousGiveawayAutoData,
        handlePrint,
        componentToPrintRef,
        ticketDistribution,
        canViewWinners,
        buttonReportDisabled,
        onChartAnimationEnd,
        activeTab
    }
}
